import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiServer, FiChevronRight, FiChevronDown, FiCheck } from 'react-icons/fi';

const API_URL = 'https://api.rontaro.com';
const FLOW_THRESHOLD = 4; // ≤4 steps → arrow flow, >4 → numbered grid

const MOCK_DATA = {
    hardware: { model: 'Raspberry Pi 4 Model B', ram: '8GB LPDDR4', storage: '256GB SSD', os: 'Ubuntu Server' },
    stats: { services: 24, since: '2021' },
    highlights: ['High availability', 'Automated backups', 'Custom monitoring'],
    architecture: [
        { label: 'Ingress', role: 'Cloudflare Tunnel + Nginx Proxy Manager' },
        { label: 'Compute', role: 'Docker Swarm (3 nodes)' }
    ],
    pipelines: [
        {
            name: 'Main Pipeline',
            steps: [{ name: 'Build' }, { name: 'Test' }, { name: 'Deploy' }],
            automation: 'GitHub Actions'
        }
    ],
    categories: [
        {
            name: 'Web Apps',
            services: [{ name: 'Portfolio', description: 'This very site' }]
        }
    ],
    updatedAt: new Date().toISOString()
};

const localize = (field, lang) => {
    if (!field) return '';
    if (typeof field === 'object') return field[lang] || field.it || field.en || '';
    return field;
};

const PipelineFlow = ({ steps, lang }) => (
    <>
        {/* Desktop: horizontal arrows */}
        <div className="hidden sm:flex items-start flex-wrap gap-y-3 gap-x-1">
            {steps.map((step, si) => (
                <React.Fragment key={si}>
                    <div className="text-center">
                        <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-sm text-sm font-medium text-amber-700 dark:text-amber-300">
                            {localize(step.name, lang)}
                        </div>
                        {step.desc && (
                            <p className="text-xs text-zinc-400 mt-1 max-w-28 mx-auto leading-tight">{localize(step.desc, lang)}</p>
                        )}
                    </div>
                    {si < steps.length - 1 && (
                        <FiChevronRight className="text-amber-400 dark:text-amber-600 mt-2.5 shrink-0" size={18} />
                    )}
                </React.Fragment>
            ))}
        </div>
        {/* Mobile: vertical */}
        <div className="flex sm:hidden flex-col gap-1.5">
            {steps.map((step, si) => (
                <React.Fragment key={si}>
                    <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-sm text-sm font-medium text-amber-700 dark:text-amber-300">
                        {localize(step.name, lang)}
                        {step.desc && <span className="text-xs text-zinc-400 font-normal ml-1.5">{localize(step.desc, lang)}</span>}
                    </div>
                    {si < steps.length - 1 && (
                        <FiChevronDown className="text-amber-400 mx-auto" size={16} />
                    )}
                </React.Fragment>
            ))}
        </div>
    </>
);

const PipelineGrid = ({ steps, lang }) => (
    <div className="grid sm:grid-cols-2 gap-2">
        {steps.map((step, si) => (
            <div key={si} className="flex items-start gap-3 p-3 bg-zinc-50 dark:bg-zinc-800 rounded-sm">
                <span className="shrink-0 w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-600 font-mono text-xs flex items-center justify-center mt-0.5">
                    {si + 1}
                </span>
                <div>
                    <p className="text-sm font-medium">{localize(step.name, lang)}</p>
                    {step.desc && <p className="text-xs text-zinc-400 dark:text-zinc-500 leading-relaxed mt-0.5">{localize(step.desc, lang)}</p>}
                </div>
            </div>
        ))}
    </div>
);

const HomeLab = () => {
    const { t, i18n } = useTranslation();
    // Resolve a JSON field that is either a plain string or { it: "...", en: "..." }
    const loc = (field) => {
        if (!field) return '';
        if (typeof field === 'object') return field[i18n.language] || field.it || field.en || '';
        return field;
    };
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        const SESSION_KEY = 'homelab_cache';
        const SESSION_TTL = 5 * 60 * 1000; // 5 minutes

        try {
            const raw = sessionStorage.getItem(SESSION_KEY);
            if (raw) {
                const { data: cached, ts } = JSON.parse(raw);
                if (Date.now() - ts < SESSION_TTL) {
                    setData(cached);
                    setLoading(false);
                    return;
                }
            }
        } catch (_) { /* ignore parse errors */ }

        fetch(`${API_URL}/api/homelab`)
            .then(r => r.ok ? r.json() : Promise.reject())
            .then(d => {
                try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ data: d, ts: Date.now() })); } catch (_) {}
                setData(d);
                setLoading(false);
            })
            .catch(() => { 
                console.warn('HomeLab API failed (CORS or Down). Using mock data for demo.');
                setData(MOCK_DATA);
                setLoading(false); 
            });
    }, []);

    if (loading) return (
        <section id="homelab" className="section">
            <div className="container-custom text-center py-20">
                <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-zinc-500">{t('homelab.loading')}</p>
            </div>
        </section>
    );

    if (!data) return (
        <section id="homelab" className="section">
            <div className="container-custom text-center py-20">
                <p className="text-zinc-400">{t('homelab.error')}</p>
            </div>
        </section>
    );

    const hw = data.hardware || {};
    const stats = data.stats || {};
    const highlights = data.highlights || [];
    const arch = data.architecture || [];
    const pipelines = data.pipelines || [];
    const categories = data.categories || [];

    return (
        <section id="homelab" className="relative section">
            {/* Background orbs for continuity */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <motion.div 
                    animate={{ x: [0, 60, 0], y: [0, -50, 0] }} 
                    transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute top-1/4 -right-20 w-80 h-80 bg-amber-200/20 dark:bg-amber-900/10 rounded-full blur-[100px]"
                />
            </div>

            <div className="container-custom relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Section Header */}
                    <div className="mb-12">
                        <span className="section-number">04 — Home Lab</span>
                        <h2 className="section-title mt-2">{t('homelab.sectionTitle')}</h2>
                        <div className="mt-6">
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-700 dark:text-green-400 font-mono text-[10px] uppercase tracking-widest shadow-sm">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                                {t('homelab.liveData')}
                            </span>
                        </div>
                    </div>

                    <div className="space-y-12">
                        {/* Hardware + Stats */}
                        <div className="grid lg:grid-cols-3 gap-6">
                            <motion.div
                                className="lg:col-span-2 terminal-card"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                            >
                                <div className="terminal-header">
                                    <span className="terminal-dot bg-red-400/80" />
                                    <span className="terminal-dot bg-amber-400/80" />
                                    <span className="terminal-dot bg-green-400/80" />
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 dark:text-zinc-500 ml-3">hardware_inspect</span>
                                </div>
                                <div className="p-8 flex flex-wrap items-center gap-6">
                                    <div className="flex items-center gap-3 text-amber-600 dark:text-amber-400 shrink-0">
                                        <div className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/20">
                                            <FiServer size={24} />
                                        </div>
                                        <div className="space-y-0.5">
                                            <span className="text-xs font-bold uppercase tracking-widest opacity-60">System</span>
                                            <p className="font-bold text-lg">{t('homelab.hardware')}</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {[hw.model, hw.ram, hw.storage, hw.os].filter(Boolean).map((v, i) => (
                                            <span key={i} className="tag px-4 py-2">
                                                {v}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>

                            {/* Stats bar */}
                            <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
                                {stats.services && (
                                    <motion.div
                                        className="terminal-card flex flex-col items-center justify-center p-6 text-center group hover:border-amber-500/50 transition-colors"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                    >
                                        <p className="text-4xl font-bold text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform">{stats.services}</p>
                                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mt-2 font-bold">{t('homelab.statsServices')}</p>
                                    </motion.div>
                                )}
                                {stats.since && (
                                    <motion.div
                                        className="terminal-card flex flex-col items-center justify-center p-6 text-center group hover:border-amber-500/50 transition-colors"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.1 }}
                                    >
                                        <p className="text-4xl font-bold text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform">{stats.since}</p>
                                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mt-2 font-bold">{t('homelab.statsSince')}</p>
                                    </motion.div>
                                )}
                            </div>
                        </div>

                        {/* Highlights & Architecture stack */}
                        <div className="flex flex-col gap-12 lg:gap-16">
                            {/* Highlights */}
                            {highlights.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                >
                                    <div className="flex items-center gap-4 mb-8">
                                        <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-50">{t('homelab.highlights')}</h3>
                                        <div className="h-px flex-1 bg-gradient-to-r from-zinc-200 to-transparent dark:from-zinc-800" />
                                    </div>
                                    <div className="grid gap-4">
                                        {highlights.map((item, i) => (
                                            <motion.div
                                                key={i}
                                                className="flex items-start gap-4 p-4 rounded-2xl bg-zinc-50/50 dark:bg-zinc-900/20 border border-zinc-200/50 dark:border-zinc-800/50"
                                                initial={{ opacity: 0, y: 15 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: i * 0.07 }}
                                            >
                                                <div className="shrink-0 w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center mt-0.5 border border-amber-500/20">
                                                    <FiCheck size={14} className="text-amber-500" />
                                                </div>
                                                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed font-medium">{loc(item)}</p>
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {/* Architecture */}
                            {arch.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.2 }}
                                >
                                    <div className="flex items-center gap-4 mb-8">
                                        <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-50">{t('homelab.architecture')}</h3>
                                        <div className="h-px flex-1 bg-gradient-to-r from-zinc-200 to-transparent dark:from-zinc-800" />
                                    </div>
                                    <div className="grid gap-3">
                                        {arch.map((item, i) => (
                                            <motion.div
                                                key={i}
                                                className="group card p-5 border-l-4 border-l-amber-500/50 hover:bg-white dark:hover:bg-zinc-800/40"
                                                initial={{ opacity: 0, y: 15 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: i * 0.08 }}
                                            >
                                                <p className="font-bold text-zinc-900 dark:text-zinc-50 mb-1 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors uppercase tracking-wider text-xs">{loc(item.label)}</p>
                                                <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">{loc(item.role)}</p>
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        {/* Pipelines */}
                        {pipelines.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                            >
                                <div className="flex items-center gap-4 mb-10">
                                    <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-50">{t('homelab.pipelines')}</h3>
                                    <div className="h-px flex-1 bg-gradient-to-r from-zinc-200 to-transparent dark:from-zinc-800" />
                                </div>
                                <div className="space-y-10">
                                    {pipelines.map((pipeline, pi) => (
                                        <motion.div
                                            key={pi}
                                            className="terminal-card overflow-hidden"
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: pi * 0.15 }}
                                        >
                                            <div className="terminal-header">
                                                <span className="terminal-dot bg-red-400/80" />
                                                <span className="terminal-dot bg-amber-400/80" />
                                                <span className="terminal-dot bg-green-400/80" />
                                                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 dark:text-zinc-500 ml-3">workflow_ci_{pi}</span>
                                            </div>
                                            <div className="p-8">
                                                <div className="flex items-center gap-3 mb-8">
                                                    <div className="w-2 h-8 bg-amber-500 rounded-full shadow-[0_0_10px_rgba(245,158,11,0.3)]" />
                                                    <p className="text-lg font-bold text-zinc-900 dark:text-zinc-50">{loc(pipeline.name)}</p>
                                                </div>

                                                <div className="relative">
                                                    {pipeline.steps.length <= FLOW_THRESHOLD
                                                        ? <PipelineFlow steps={pipeline.steps} lang={i18n.language} />
                                                        : <PipelineGrid steps={pipeline.steps} lang={i18n.language} />
                                                    }
                                                </div>

                                                {pipeline.automation && (
                                                    <div className="mt-8 pt-6 border-t border-zinc-100 dark:border-zinc-800 flex items-center gap-3">
                                                        <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Automation:</span>
                                                        <p className="text-xs text-amber-600 dark:text-amber-400 font-medium italic">
                                                            {loc(pipeline.automation)}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* Services Tabs */}
                        {categories.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                            >
                                <div className="flex items-center gap-4 mb-10">
                                    <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-50">{t('homelab.services')}</h3>
                                    <div className="h-px flex-1 bg-gradient-to-r from-zinc-200 to-transparent dark:from-zinc-800" />
                                </div>
                                
                                <div className="terminal-card overflow-hidden">
                                    <div className="terminal-header">
                                        <span className="terminal-dot bg-red-400/80" />
                                        <span className="terminal-dot bg-amber-400/80" />
                                        <span className="terminal-dot bg-green-400/80" />
                                        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 dark:text-zinc-500 ml-3">service_manager</span>
                                    </div>
                                    <div className="p-0">
                                        <div className="flex overflow-x-auto no-scrollbar bg-zinc-50/50 dark:bg-zinc-800/40 border-b border-zinc-200 dark:border-zinc-700/50">
                                            {categories.map((cat, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => setActiveTab(i)}
                                                    className={`px-8 py-5 text-xs font-bold uppercase tracking-widest transition-all relative shrink-0 ${
                                                        activeTab === i
                                                            ? 'text-amber-600 dark:text-amber-400 bg-white/50 dark:bg-zinc-900/50'
                                                            : 'text-zinc-500 dark:text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300'
                                                    }`}
                                                >
                                                    {loc(cat.name)}
                                                    {activeTab === i && (
                                                        <motion.div 
                                                            layoutId="activeTab"
                                                            className="absolute bottom-0 left-0 right-0 h-1 bg-amber-500" 
                                                        />
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="p-8">
                                            <motion.div
                                                key={activeTab}
                                                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                {categories[activeTab]?.services.map((svc, i) => (
                                                    <div key={i} className="p-5 bg-zinc-50/50 dark:bg-zinc-800/20 border border-zinc-200/50 dark:border-zinc-700/50 rounded-2xl group hover:border-amber-500/30 transition-colors">
                                                        <p className="font-bold text-zinc-900 dark:text-zinc-50 mb-2 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">{loc(svc.name)}</p>
                                                        <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed line-clamp-2">{loc(svc.description)}</p>
                                                    </div>
                                                ))}
                                            </motion.div>
                                            {categories[activeTab]?.description && (
                                                <div className="mt-8 p-4 rounded-xl bg-amber-500/5 border border-amber-500/10">
                                                    <p className="text-xs text-amber-700 dark:text-amber-500/80 leading-relaxed font-medium">
                                                        <span className="font-bold uppercase tracking-wider mr-2">Note:</span>
                                                        {loc(categories[activeTab].description)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Updated at */}
                        {data.updatedAt && (
                            <p className="text-center text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-600 pt-8">
                                {t('homelab.updatedAt')} {new Date(data.updatedAt).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default HomeLab;
